import { useQuery } from '@tanstack/react-query';
import FilterChip from 'components/buttons/FilterChip';
import { Page, PageContent } from 'components/layout/page';
import { CaregiverGrid } from 'components/messaging/TeamSection';
import PageTopBar from 'components/navigation/PageTopBar';
import { Empty, EmptyDescription, EmptyHeader } from 'components/ui/empty';
import { Skeleton } from 'components/ui/skeleton';
import { useRegisteredCaregivers } from 'hooks/useCaregivers';
import posthog from 'posthog-js';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ApiCaregiversManager from 'services/api/ApiCaregiversManager';
import { CaregiverGroupDTO } from 'services/api/models/CaregiverGroupDTO';

/**
 * Every caregiver a patient can be oriented to, filtered by specialty
 * (Figma 3c-MessagingTeam): « Voir toutes nos expertes » on the messaging page.
 */
export default function MessagingTeam() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: caregivers, isPending } = useRegisteredCaregivers();
  // The groups patients may see (not the team's internal ones)
  const { data: visibleGroups } = useQuery({
    queryKey: ['caregiver-groups'],
    queryFn: () => ApiCaregiversManager.getCaregiverGroupsList(),
    staleTime: 1000 * 60 * 10,
  });
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  const groups = useMemo(() => {
    const visibleIds = new Set(visibleGroups?.map(g => g.id));
    const byId = new Map<string, CaregiverGroupDTO>();
    caregivers?.forEach(c => c.caregiver.caregiver_groups.forEach(g => visibleIds.has(g.id) && byId.set(g.id, g)));
    return [...byId.values()].sort((a, b) => a.display_name.localeCompare(b.display_name));
  }, [caregivers, visibleGroups]);

  const shownCaregivers = (caregivers ?? []).filter(
    c => selectedGroupIds.length === 0 || c.caregiver.caregiver_groups.some(g => selectedGroupIds.includes(g.id)),
  );

  // Several specialties can be selected at once; selecting one again removes it, « Toutes » removes them all
  const showAllGroups = () => {
    posthog.capture('messaging.team_filtered', { selected: false, all: true });
    setSelectedGroupIds([]);
  };
  const toggleGroup = (groupId: string) => {
    const selected = !selectedGroupIds.includes(groupId);
    posthog.capture('messaging.team_filtered', { selected });
    setSelectedGroupIds(selected ? [...selectedGroupIds, groupId] : selectedGroupIds.filter(id => id !== groupId));
  };

  return (
    <Page>
      <PageTopBar title={t('messagingV3.team.pageBack')} backButton onBackButtonClicked={() => navigate(-1)} />
      <PageContent className="gap-8 pb-10">
        <div className="flex flex-col gap-2">
          <h2>{t('messagingV3.team.pageTitle')}</h2>
          <p className="text-base">{t('messagingV3.team.pageText')}</p>
        </div>
        {isPending ? (
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-52" />
            <Skeleton className="h-52" />
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2.5">
              <FilterChip
                selected={selectedGroupIds.length === 0}
                removable={false}
                data-testid="team-chip-all"
                onClick={showAllGroups}
              >
                {t('messagingV3.team.all')}
              </FilterChip>
              {groups.map(group => (
                <FilterChip
                  key={group.id}
                  selected={selectedGroupIds.includes(group.id)}
                  data-testid={`team-chip-${group.name}`}
                  onClick={() => toggleGroup(group.id)}
                >
                  {group.display_name}
                </FilterChip>
              ))}
            </div>
            {shownCaregivers.length > 0 ? (
              <CaregiverGrid caregivers={shownCaregivers} />
            ) : (
              <Empty>
                <EmptyHeader>
                  <EmptyDescription>{t('messagingV3.team.empty')}</EmptyDescription>
                </EmptyHeader>
              </Empty>
            )}
          </>
        )}
      </PageContent>
    </Page>
  );
}
